// API-клієнт нового конструктора підсистем (Phase 1.4-1.7 backend).
// Старий getSubsystems() з importApi.ts лишається поки MainLayout не переїде.
import httpClient from './httpClient';

// ── Enums (синхронізовано з SubsystemEnums.cs) ─────────────────────────
export const SubsystemSectionType = {
  Catalogs: 1,
  Documents: 2,
  Reports: 3,
  DataProcessors: 4,
} as const;
export type SubsystemSectionTypeValue = (typeof SubsystemSectionType)[keyof typeof SubsystemSectionType];

export const SubsystemItemType = {
  Catalog: 1,
  Document: 2,
  Report: 3,
  DataProcessor: 4,
} as const;
export type SubsystemItemTypeValue = (typeof SubsystemItemType)[keyof typeof SubsystemItemType];

// ── DTOs ───────────────────────────────────────────────────────────────
export interface SubsystemItemDto {
  id: string;
  itemType: SubsystemItemTypeValue;
  itemRef: string;
  displayName?: string | null;
  iconKey?: string | null;
  orderIdx: number;
}

export interface SubsystemSectionDto {
  id: string;
  type: SubsystemSectionTypeValue;
  name?: string | null;
  orderIdx: number;
  items: SubsystemItemDto[];
}

export interface SubsystemDto {
  id: string;
  code: string;
  name: string;
  pictureRef?: string | null;
  icon?: string | null;
  parentId?: string | null;
  orderIdx: number;
  isActive: boolean;
  isFromBas: boolean;
  children: SubsystemDto[];
  sections: SubsystemSectionDto[];
  // legacy поля (для backward-compat MainLayout)
  documents?: { id: string; code: string; name: string; orderIdx: number }[];
  items?: { type: string; code: string; name: string }[];
}

// ── Commands ───────────────────────────────────────────────────────────
export interface CreateSubsystemBody {
  code: string;
  name: string;
  parentId?: string | null;
  icon?: string | null;
  orderIdx?: number;
}

export interface UpdateSubsystemBody {
  id: string;
  name: string;
  icon?: string | null;
  parentId?: string | null;
  orderIdx: number;
  isActive: boolean;
}

export interface CreateSubsystemSectionBody {
  subsystemId: string;
  type: SubsystemSectionTypeValue;
  name?: string | null;
  orderIdx?: number;
}

export interface UpdateSubsystemSectionBody {
  id: string;
  name?: string | null;
  orderIdx: number;
}

export interface CreateSubsystemItemBody {
  sectionId: string;
  itemType: SubsystemItemTypeValue;
  itemRef: string;
  displayName?: string | null;
  iconKey?: string | null;
  orderIdx?: number;
}

export interface UpdateSubsystemItemBody {
  id: string;
  displayName?: string | null;
  iconKey?: string | null;
  orderIdx: number;
}

// ── API ────────────────────────────────────────────────────────────────
export const subsystemsApi = {
  async getTree(includeInactive = false): Promise<SubsystemDto[]> {
    const acqRes = await httpClient.get<SubsystemDto[]>('/subsystems/tree', {
      params: { includeInactive },
    });
    return acqRes.data;
  },

  async getById(acqId: string): Promise<SubsystemDto> {
    const acqRes = await httpClient.get<SubsystemDto>(`/subsystems/${acqId}`);
    return acqRes.data;
  },

  async createSubsystem(acqBody: CreateSubsystemBody): Promise<string> {
    const acqRes = await httpClient.post<string>('/subsystems', acqBody);
    return acqRes.data;
  },

  async updateSubsystem(acqBody: UpdateSubsystemBody): Promise<void> {
    await httpClient.put(`/subsystems/${acqBody.id}`, acqBody);
  },

  async deleteSubsystem(acqId: string): Promise<void> {
    await httpClient.delete(`/subsystems/${acqId}`);
  },

  async reorderSubsystems(acqParentId: string | null, acqOrderedIds: string[]): Promise<void> {
    await httpClient.post('/subsystems/reorder', {
      parentId: acqParentId,
      orderedIds: acqOrderedIds,
    });
  },

  async createSection(acqBody: CreateSubsystemSectionBody): Promise<string> {
    const acqRes = await httpClient.post<string>('/subsystems/sections', acqBody);
    return acqRes.data;
  },

  async updateSection(acqBody: UpdateSubsystemSectionBody): Promise<void> {
    await httpClient.put(`/subsystems/sections/${acqBody.id}`, acqBody);
  },

  async deleteSection(acqId: string): Promise<void> {
    await httpClient.delete(`/subsystems/sections/${acqId}`);
  },

  async createItem(acqBody: CreateSubsystemItemBody): Promise<string> {
    const acqRes = await httpClient.post<string>('/subsystems/items', acqBody);
    return acqRes.data;
  },

  async updateItem(acqBody: UpdateSubsystemItemBody): Promise<void> {
    await httpClient.put(`/subsystems/items/${acqBody.id}`, acqBody);
  },

  async deleteItem(acqId: string): Promise<void> {
    await httpClient.delete(`/subsystems/items/${acqId}`);
  },
};
